import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export const HeaderContainer = styled.header`
  /* Header palette. Light mode: page-colored bar, brown logo/text, brown rule underneath.
     Dark mode (below) keeps a dark green bar with gold accents. Everything inside
     reads these variables, so the palette is changed in one place. */
  --hdr-bg: var(--background-secondary); /* same warm off-white as the page (#F7F5F1) */
  --hdr-rule: 3px solid var(--brand-brown-hover);
  --hdr-wordmark: var(--brand-brown-deep);
  --hdr-logo: var(--brand-brown);
  --hdr-logo-hover: var(--brand-brown-hover);
  --hdr-text: var(--text-primary);
  --hdr-strong: var(--brand-brown-deep);
  --hdr-muted: var(--brand-brown-deep);
  --hdr-icon: var(--brand-brown-deep);
  --hdr-accent: var(--brand-brown);
  --hdr-accent-hover: var(--brand-brown-hover);
  --hdr-icon-fill: var(--brand-brown-deep);
  --hdr-icon-lines: #FFFFFF;
  --hdr-avatar-bg: var(--brand-brown);
  --hdr-avatar-text: #FFFFFF;
  --hdr-role-admin: var(--brand-brown);
  --hdr-hover-bg: rgba(122, 92, 53, 0.08);

  body.dark-mode & {
    --hdr-bg: var(--brand-green);
    --hdr-rule: 1px solid rgba(232, 201, 139, 0.25);
    --hdr-wordmark: #FFFFFF;
    --hdr-logo: #FFFFFF;
    --hdr-logo-hover: var(--brand-gold);
    --hdr-text: #FFFFFF;
    --hdr-strong: #FFFFFF;
    --hdr-muted: rgba(255, 255, 255, 0.7);
    --hdr-icon: rgba(255, 255, 255, 0.82);
    --hdr-accent: var(--brand-gold);
    --hdr-accent-hover: var(--brand-gold);
    --hdr-icon-fill: var(--brand-gold);
    --hdr-icon-lines: var(--brand-green);
    --hdr-avatar-bg: var(--brand-gold);
    --hdr-avatar-text: var(--brand-green);
    --hdr-role-admin: var(--brand-gold);
    --hdr-hover-bg: rgba(255, 255, 255, 0.1);
  }

  background-color: var(--hdr-bg);
  border-bottom: var(--hdr-rule);
  padding: 0 1.5rem;
  height: 64px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-template-columns: none;
  }
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: start;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const DonateLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: var(--hdr-strong);
  text-decoration: none;
  font-size: 1.0625rem;
  font-weight: 600;
  font-family: 'Inter', sans-serif;

  .hdr-icon-base {
    color: var(--hdr-accent);
  }

  /* Inverse-on-hover icons (see HeaderIcon): the icon fills with a dark shape (.hdr-icon-base)
     and the same icon is drawn again on top as thin lines (.hdr-icon-lines), so detail inside
     the shape shows as light lines instead of vanishing into a solid blob. */
  .hdr-icon-base {
    fill: transparent;
    transition: color 0.2s ease, fill 0.2s ease, stroke-width 0.2s ease;
  }

  .hdr-icon-lines {
    fill: none;
    stroke: var(--hdr-icon-lines);
    stroke-width: 1.25;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .hdr-icon-base,
  &:focus-visible .hdr-icon-base {
    color: var(--hdr-icon-fill);
    fill: var(--hdr-icon-fill);
    stroke-width: 2.8;
  }

  &:hover .hdr-icon-lines,
  &:focus-visible .hdr-icon-lines {
    opacity: 1;
  }

  /* The text keeps its color on hover; only the heart changes. The global a:hover rule
     recolors every link, so the color is pinned here for every link state. */
  &:hover,
  &:active,
  &:visited {
    color: var(--hdr-strong);
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid var(--hdr-accent-hover);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: none; /* Mobile uses MobileDonateIcon in the header's right section */
  }
`;

// Mobile: a heart icon in the header replaces the separate Donate strip below it.
export const MobileDonateIcon = styled(Link)`
  display: none;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  color: var(--hdr-accent);

  /* Inverse-on-hover icons (see HeaderIcon): the icon fills with a dark shape (.hdr-icon-base)
     and the same icon is drawn again on top as thin lines (.hdr-icon-lines), so detail inside
     the shape shows as light lines instead of vanishing into a solid blob. */
  .hdr-icon-base {
    fill: transparent;
    transition: color 0.2s ease, fill 0.2s ease, stroke-width 0.2s ease;
  }

  .hdr-icon-lines {
    fill: none;
    stroke: var(--hdr-icon-lines);
    stroke-width: 1.25;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .hdr-icon-base,
  &:focus-visible .hdr-icon-base {
    color: var(--hdr-icon-fill);
    fill: var(--hdr-icon-fill);
    stroke-width: 2.8;
  }

  &:hover .hdr-icon-lines,
  &:focus-visible .hdr-icon-lines {
    opacity: 1;
  }

  &:hover {
    background-color: var(--hdr-hover-bg);
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;

  h1 {
    color: var(--hdr-wordmark);
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.5rem;
    font-weight: 600; /* the serif is thin at 500 and washes out on a light bar */
    letter-spacing: -0.025em;
    transition: color 0.2s ease;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  &:hover h1 {
    color: var(--hdr-logo-hover);
  }

  span {
    color: var(--text-secondary);
    font-size: 0.875rem;
    font-family: 'Inter', sans-serif;
  }
`;

// The emblem: the logo file is only the shape (a mask), so its color is set here
// (brown, or white in dark mode) and changes on hover along with the wordmark.
export const LogoMark = styled.span`
  display: inline-block;
  height: 40px;
  aspect-ratio: 80 / 114;
  flex-shrink: 0;
  background-color: var(--hdr-logo);
  -webkit-mask: url(${(p) => p.$src}) center / contain no-repeat;
  mask: url(${(p) => p.$src}) center / contain no-repeat;
  transition: background-color 0.2s ease;

  ${Logo}:hover & {
    background-color: var(--hdr-logo-hover);
  }

  @media (max-width: 768px) {
    height: 32px;
  }
`;


export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: end;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const NotificationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--hdr-icon);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  position: relative;

  /* Inverse-on-hover icons (see HeaderIcon): the icon fills with a dark shape (.hdr-icon-base)
     and the same icon is drawn again on top as thin lines (.hdr-icon-lines), so detail inside
     the shape shows as light lines instead of vanishing into a solid blob. */
  .hdr-icon-base {
    fill: transparent;
    transition: color 0.2s ease, fill 0.2s ease, stroke-width 0.2s ease;
  }

  .hdr-icon-lines {
    fill: none;
    stroke: var(--hdr-icon-lines);
    stroke-width: 1.25;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover .hdr-icon-base,
  &:focus-visible .hdr-icon-base {
    color: var(--hdr-icon-fill);
    fill: var(--hdr-icon-fill);
    stroke-width: 2.8;
  }

  &:hover .hdr-icon-lines,
  &:focus-visible .hdr-icon-lines {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--hdr-accent-hover);
    outline-offset: 2px;
  }
`;

// Holds the two stacked copies of an icon (see HeaderIcon).
export const IconSlot = styled.span`
  position: relative;
  display: inline-block;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex-shrink: 0;

  svg {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

export const NotificationBadge = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  background-color: var(--secondary-color);
  color: var(--text-inverse);
  font-size: 0.75rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0;
  min-width: 18px;
  text-align: center;
`;

export const UserMenu = styled.div`
  position: relative;
`;

export const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background-color: var(--hdr-hover-bg);
  }
`;

export const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

export const FallbackAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--hdr-avatar-bg);
  color: var(--hdr-avatar-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

export const UserInfo = styled.div`
  text-align: left;

  .name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--hdr-text);
  }

  .role {
    font-size: 0.75rem;
    color: var(--hdr-muted);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;

  @media (max-width: 768px) {
    right: -1rem;
    min-width: 180px;
  }
`;

export const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: var(--transition);
  color: var(--text-primary);
  font-size: 0.875rem;

  &:hover {
    background-color: var(--background-secondary);
  }

  &:first-child {
    border-bottom: 1px solid var(--border-color);
  }
`;

export const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--hdr-icon);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background-color: var(--hdr-hover-bg);
    color: var(--hdr-accent-hover);
  }
`;
